import { StyleSheet, Font } from "@react-pdf/renderer";

Font.register({
    family: "SourceSansPro", fonts: [
        { src: require("../../assets/fonts/SourceSansPro-Regular.ttf") },
        { src: require("../../assets/fonts/SourceSansPro-Bold.ttf"), fontWeight: "bold" },
    ],
});

// react-pdf hyphenates English words by default (Knuth-Liang en-US patterns),
// which breaks words mid-line with a stray hyphen e.g. "apprentice-ship".
// Register a no-op callback so words wrap whole instead of being split.
Font.registerHyphenationCallback((word) => [word]);

const colors = {
    midnight:  "#0B1729",
    ink:       "#1A1F2E",
    inkMuted:  "#6A7280",
    inkSubtle: "#8B92A0",
    rule:      "#D7DAE0",
    surface:   "#FFFFFF",
    coral:     "#E85533",
    link:      "#1F5C8C",
};

export const styles = StyleSheet.create({
    page: {
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.surface,
        paddingTop: 44,
        paddingBottom: 44,
        paddingHorizontal: 44,
        fontFamily: "SourceSansPro",
        color: colors.ink,
    },

    /* ---------- HEADER ---------- */
    metaContainer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 18,
    },
    metaContentContainer: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
    },
    metaBasicInformationContainer: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
    },
    fullName: {
        fontFamily: "SourceSansPro",
        fontWeight: "bold",
        fontSize: 24,
        letterSpacing: 0.5,
        color: colors.midnight,
        lineHeight: 1.1,
    },
    desiredPosition: {
        fontFamily: "SourceSansPro",
        fontSize: 9.5,
        fontWeight: "bold",
        letterSpacing: 1.6,
        marginTop: 3,
        marginBottom: 14,
        color: colors.coral,
    },
    profileImageContainer: {
        paddingLeft: 24,
    },
    profileImage: {
        width: 128,
        height: 128,
        objectFit: "cover",
    },

    /* ---------- TEXT ROW (label / value pairs) ---------- */
    textRowContainer: {
        flexDirection: "row",
        alignItems: "flex-start",
        paddingVertical: 2.5,
    },
    textRowContainerImage: {
        width: 10,
        height: 10,
        marginLeft: 6,
        marginTop: 3,
        objectFit: "contain",
    },
    textRowPropContainer: {
        flexDirection: "row",
        width: 104,
    },
    textRowPropName: {
        fontSize: 9.5,
        fontWeight: "normal",
        letterSpacing: 0.3,
        color: colors.inkMuted,
    },
    textRowValueContainer: {
        flexDirection: "row",
    },
    textRowValue: {
        fontSize: 10.5,
        color: colors.ink,
    },
    textRowValueLink: {
        fontSize: 10.5,
        color: colors.link,
        textDecoration: "none",
    },
    textRowMultipleTextContainer: {
        flexDirection: "column",
    },
    textRowMultipleText: {
        fontSize: 10.5,
        color: colors.ink,
    },
    textRowMultipleDate: {
        fontSize: 8,
        color: colors.inkSubtle,
        marginLeft: 5,
        marginTop: 2,
    },

    /* ---------- SECTION SEPARATOR ---------- */
    sectionSeparatorContainer: {
        flexDirection: "row",
        alignItems: "center",
        marginTop: 22,
        marginBottom: 10,
    },
    sectionSeparatorBar: {
        width: 10,
        height: 1.5,
        backgroundColor: colors.coral,
        marginRight: 8,
    },
    sectionSeparatorTitle: {
        fontFamily: "SourceSansPro",
        fontWeight: "bold",
        fontSize: 9.5,
        letterSpacing: 2,
        color: colors.midnight,
    },
    sectionSeparatorRule: {
        flex: 1,
        height: 0.5,
        backgroundColor: colors.rule,
        marginLeft: 10,
    },

    /* ---------- SECTION LAYOUT ---------- */
    sectionMasterContainer: {
        width: "100%",
    },
    sectionContainer: {
        width: "100%",
        flexDirection: "row",
    },
    sectionListContainer: {
        flex: 1,
        flexDirection: "column",
    },
    paragraph: {
        fontSize: 10.5,
        lineHeight: 1.45,
        color: colors.ink,
    },

    /* ---------- LIST ITEMS (skills, equipment, quals) ---------- */
    listItemText: {
        fontSize: 10.5,
        lineHeight: 1.5,
        color: colors.ink,
    },

    /* ---------- TIMELINE ITEMS (experience, education) ---------- */
    listItemContainer: {
        paddingBottom: 10,
        flexDirection: "row",
    },
    sectionDateContainer: {
        flexDirection: "column",
        width: 84,
        paddingTop: 2,
        paddingRight: 15,
    },
    sectionDateText: {
        fontSize: 10.5,
        color: colors.ink,
    },
    sectionItemContentContainer: {
        flexDirection: "column",
        width: "100%",
    },
    sectionItemContentTitleContainer: {
        flexDirection: "row",
        alignItems: "flex-start",
        paddingBottom: 4,
    },
    sectionItemContentTitle: {
        fontWeight: "bold",
        fontSize: 10.5,
        color: colors.midnight,
        letterSpacing: 0.2,
    },
    timelineTitleMain: {
        flex: 1,
        paddingRight: 16,
    },
    timelineRole: {
        fontFamily: "SourceSansPro",
        fontWeight: "bold",
        fontSize: 10.5,
        color: colors.midnight,
        letterSpacing: 0.2,
    },
    timelineCompany: {
        fontFamily: "SourceSansPro",
        fontSize: 10.5,
        color: colors.inkMuted,
        marginTop: 1,
    },
    timelineDateContainer: {
        width: 105,
        alignItems: "flex-end",
        paddingTop: 2,
    },
    timelineDate: {
        fontFamily: "SourceSansPro",
        fontSize: 9.5,
        color: colors.inkMuted,
        letterSpacing: 0.3,
    },

    /* ---------- BULLET LIST (skills, equipment, quals) ---------- */
    bulletRow: {
        flexDirection: "row",
        paddingBottom: 3,
        alignItems: "flex-start",
    },
    bullet: {
        fontFamily: "SourceSansPro",
        fontSize: 11,
        color: colors.coral,
        width: 10,
    },
    bulletContent: {
        flex: 1,
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "flex-start",
    },
    bulletText: {
        fontFamily: "SourceSansPro",
        fontSize: 10.5,
        color: colors.ink,
        lineHeight: 1.4,
    },
    bulletDate: {
        fontFamily: "SourceSansPro",
        fontSize: 8,
        color: colors.inkSubtle,
        marginLeft: 5,
        paddingTop: 2.5,
    },

    /* ---------- REFERENCES (restructured) ---------- */
    referenceCompany: {
        fontFamily: "SourceSansPro",
        fontWeight: "bold",
        fontSize: 10.5,
        color: colors.midnight,
        letterSpacing: 0.2,
    },
    referencePerson: {
        fontFamily: "SourceSansPro",
        fontSize: 10.5,
        color: colors.inkMuted,
        marginTop: 1,
    },
    referenceContactRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "center",
        marginTop: 3,
    },
    referenceContactLink: {
        fontFamily: "SourceSansPro",
        fontSize: 9.5,
        color: colors.link,
        textDecoration: "none",
    },
    referenceContact: {
        fontFamily: "SourceSansPro",
        fontSize: 9.5,
        color: colors.ink,
    },
    referenceContactSep: {
        fontFamily: "SourceSansPro",
        fontSize: 9.5,
        color: colors.inkSubtle,
        marginHorizontal: 6,
    },
    sectionItemContentDescriptionContainer: {
        flexDirection: "row",
    },
    sectionItemContentDescription: {
        fontSize: 10.5,
        lineHeight: 1.45,
        color: colors.ink,
    },

    /* ---------- REFERENCES ---------- */
    referenceItemContainer: {
        paddingTop: 6,
        paddingBottom: 8,
        borderBottomWidth: 0.5,
        borderBottomColor: colors.rule,
    },
    referenceItemTitleContainer: {
        flexDirection: "column",
        paddingBottom: 3,
    },
    referenceItemTitle: {
        fontWeight: "bold",
        fontSize: 10.5,
        color: colors.midnight,
        letterSpacing: 0.2,
    },
});
